import React, { useState, useEffect } from "react";
import { Form, Button, Card, message, Spin, Tabs, Row, Col, Typography, Tag, Select, Popconfirm } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
  createApprovalTemplate,
  fetchApprovalTemplates,
  updateApprovalTemplate,
  updateTemplateStatus,
  fetchUsers,
  fetchEntityTypes,
  fetchAssignmentStrategies,
  fetchPermissionRoles,
  fetchFactCatalogue,
} from "../../api";
import ModuleDetailsFormSection from "./components/ModuleDetailsFormSection";
import ApprovalRulesSection from "./components/ApprovalRulesSection";
import ApproversSection from "./components/ApproversSection";
import { usePermissions } from "../../../../hooks/usePermissions";
import { ModuleType } from "../../../../components";

const { Text, Title } = Typography;

// Fixed lifecycle steps for INVENTORY_TRANSFER_APPROVAL entity type
const INVENTORY_TRANSFER_LIFECYCLE_STEPS = [
  { stepName: 'Approval', approvalMode: 'SEQUENTIAL', escalationStrategy: 'SKIP', commentsRequired: false, resolvers: [{}], deadlineHours: 24 },
  { stepName: 'Packing', approvalMode: 'SEQUENTIAL', escalationStrategy: 'REASSIGN', commentsRequired: false, resolvers: [{}], deadlineHours: 24 },
  { stepName: 'Dispatch', approvalMode: 'SEQUENTIAL', escalationStrategy: 'REASSIGN', commentsRequired: false, resolvers: [{}], deadlineHours: 24 },
  { stepName: 'Receive', approvalMode: 'SEQUENTIAL', escalationStrategy: 'REASSIGN', commentsRequired: false, resolvers: [{}], deadlineHours: 24 },
  { stepName: 'Verification', approvalMode: 'SEQUENTIAL', escalationStrategy: 'REASSIGN', commentsRequired: false, resolvers: [{}], deadlineHours: 24 },
  { stepName: 'Closure', approvalMode: 'SEQUENTIAL', escalationStrategy: 'REASSIGN', commentsRequired: false, resolvers: [{}], deadlineHours: 24 },
];

const ApprovalTemplateForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [templateStatus, setTemplateStatus] = useState('DRAFT');

  // Data for dropdowns
  const [userOptions, setUserOptions] = useState([]);
  const [entityTypeOptions, setEntityTypeOptions] = useState([]);
  const [strategyOptions, setStrategyOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  // Fact catalogue for the rule builder
  const [factCatalogue, setFactCatalogue] = useState([]);
  const [selectedEntityType, setSelectedEntityType] = useState(null);
  
  const [activeTab, setActiveTab] = useState("1");
  const navigate = useNavigate();
  const { id } = useParams();
  const { canEditField } = usePermissions();
  const module = ModuleType.APPROVALS;
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    // Fetch dropdown data concurrently
    const loadDropdownData = async () => {
      try {
        const [usersRes, entitiesRes, strategiesRes, rolesRes, factsRes] = await Promise.all([
          fetchUsers().catch(() => []),
          fetchEntityTypes().catch(() => []),
          fetchAssignmentStrategies().catch(() => []),
          fetchPermissionRoles().catch(() => []),
          fetchFactCatalogue().catch(() => []),
        ]);

        setUserOptions((usersRes.data || usersRes || []).map((u) => ({
          label: u.name || u.email || u._id,
          value: u._id,
        })));

        setEntityTypeOptions((entitiesRes || []).map((e) => ({
          label: e.displayName || e.entityType,
          value: e.entityType,
        })));

        setStrategyOptions((strategiesRes || []).map((s) => ({
          label: s.name,
          value: s.id,
        })));

        setRoleOptions((rolesRes || []).map((r) => ({
          label: r.name,
          value: r.key,
        })));

        setFactCatalogue(factsRes || []);
      } catch (err) {
        console.error("Failed to load dropdown data", err);
      }
    };
    loadDropdownData();
  }, []);

  // If editing, fetch template and prefill
  useEffect(() => {
    if (id) {
      setLoading(true);
      fetchApprovalTemplates()
        .then((templates) => {
          const tpl = (templates || []).find((t) => t._id === id);
          if (tpl) {
            setTemplateStatus(tpl.status || (tpl.isActive ? 'ACTIVE' : 'DRAFT'));
            setSelectedEntityType(tpl.entityType || tpl.module);
            form.setFieldsValue({
              entityType: tpl.entityType || tpl.module,
              name: tpl.name,
              description: tpl.description,
              autoApprove: tpl.autoApprove || false,
              steps: tpl.steps.map((step) => {
                // Normalize to resolvers array for the new multi-resolver UI
                let resolvers;
                if (step.resolverConfigs && step.resolverConfigs.length > 0) {
                  resolvers = step.resolverConfigs.map(rc => ({
                    assignmentStrategy: rc.assignmentStrategy,
                    resolverConfig: rc.resolverConfig || {},
                  }));
                } else {
                  resolvers = [{
                    assignmentStrategy: step.assignmentStrategy || (step.type === 'user' ? 'SPECIFIC_USER' : 'PERMISSION_ROLE'),
                    resolverConfig: step.resolverConfig || { userId: step.user, roleKey: step.role, contextLevel: step.context || 'branch' },
                  }];
                }
                return {
                  stepName: step.stepName,
                  resolvers,
                  approvalMode: step.approvalMode || 'SEQUENTIAL',
                  escalationStrategy: step.escalationStrategy || 'REASSIGN',
                  commentsRequired: step.commentsRequired || false,
                  capturesPaymentReference: step.capturesPaymentReference || false,
                  deadlineHours: step.deadlineHours,
                };
              }),
              rules: tpl.rules,
            });
          } else {
            messageApi.error("Template not found");
            navigate("/approvals/approval-templates");
          }
        })
        .catch(() => messageApi.error("Failed to fetch template"))
        .finally(() => setLoading(false));
    }
  }, [id, form, navigate, messageApi]);

  const onFinish = async (values) => {
    // Map data to backend schema
    const isAutoApprove = values.autoApprove === true;
    const payload = {
      entityType: values.entityType,
      module: values.entityType, // Backwards compatibility
      name: values.name,
      description: values.description,
      autoApprove: isAutoApprove,
      rules: values.rules,
      steps: isAutoApprove
        ? []
        : (values.steps || []).map((step, idx) => ({
            stepNumber: idx + 1,
            stepName: step.stepName,
            resolverConfigs: (step.resolvers || []).map(r => ({
              assignmentStrategy: r.assignmentStrategy,
              resolverConfig: r.resolverConfig || {},
            })),
            // Keep single-resolver fields populated for backward compat with older instances
            assignmentStrategy: step.resolvers?.[0]?.assignmentStrategy,
            resolverConfig: step.resolvers?.[0]?.resolverConfig || {},
            approvalMode: step.approvalMode,
            escalationStrategy: step.escalationStrategy,
            commentsRequired: step.commentsRequired,
            // Payment workflows: the step whose approvers actually make the
            // transfer, and are therefore asked for the bank UTR.
            capturesPaymentReference: !!step.capturesPaymentReference,
            deadlineHours: step.deadlineHours || 24,
          })),
    };
    try {
      if (id) {
        await updateApprovalTemplate(id, payload);
        messageApi.success("Approval template updated!");
      } else {
        await createApprovalTemplate(payload);
        messageApi.success("Approval template created!");
      }
      navigate("/approvals/approval-templates");
    } catch (err) {
      messageApi.error("Failed to save template");
    }
  };

  const handleCloneAsNew = async () => {
    form.validateFields().then(async (values) => {
      const isAutoApprove = values.autoApprove === true;
      const payload = {
        entityType: values.entityType,
        module: values.entityType,
        name: values.name + " (Clone)",
        description: values.description,
        autoApprove: isAutoApprove,
        rules: values.rules,
        steps: isAutoApprove
          ? []
          : (values.steps || []).map((step, idx) => ({
              stepNumber: idx + 1,
              stepName: step.stepName,
              resolverConfigs: (step.resolvers || []).map(r => ({
                assignmentStrategy: r.assignmentStrategy,
                resolverConfig: r.resolverConfig || {},
              })),
              assignmentStrategy: step.resolvers?.[0]?.assignmentStrategy,
              resolverConfig: step.resolvers?.[0]?.resolverConfig || {},
              approvalMode: step.approvalMode,
              escalationStrategy: step.escalationStrategy,
              commentsRequired: step.commentsRequired,
              // Payment workflows: the step whose approvers actually make the
              // transfer, and are therefore asked for the bank UTR.
              capturesPaymentReference: !!step.capturesPaymentReference,
              deadlineHours: step.deadlineHours || 24,
            })),
      };
      try {
        await createApprovalTemplate(payload);
        messageApi.success("New template version cloned successfully!");
        navigate("/approvals/approval-templates");
      } catch (err) {
        messageApi.error("Failed to clone template");
      }
    });
  };

  const handleStatusChange = async (nextStatus) => {
    if (!id) return;
    try {
      const updated = await updateTemplateStatus(id, nextStatus);
      setTemplateStatus(updated.status);
      messageApi.success(`Template set to ${nextStatus}`);
    } catch {
      messageApi.error("Failed to update status");
    }
  };

  const STATUS_CONFIG = {
    ACTIVE:   { color: "success", label: "Active" },
    INACTIVE: { color: "warning", label: "Inactive" },
    DRAFT:    { color: "default", label: "Draft" },
  };
  const STATUS_TRANSITIONS = {
    DRAFT:    [{ label: "Activate", next: "ACTIVE" }],
    ACTIVE:   [{ label: "Deactivate", next: "INACTIVE" }, { label: "Set Draft", next: "DRAFT" }],
    INACTIVE: [{ label: "Activate", next: "ACTIVE" }, { label: "Set Draft", next: "DRAFT" }],
  };

  if (loading) {
    return <Spin style={{ margin: 40 }} />;
  }

  const noPermission = (
    <div className="text-center mt-5">
      <Text type="danger">You do not have permission to access this section.</Text>
    </div>
  );

  const statusCfg = STATUS_CONFIG[templateStatus] || STATUS_CONFIG.DRAFT;
  const statusTransitions = id ? (STATUS_TRANSITIONS[templateStatus] || []) : [];

  return (
    <Card
      title={
        <Row align="middle" gutter={12}>
          <Col>{id ? "Edit Workflow Template" : "Create Workflow Template"}</Col>
          <Col>
            <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
          </Col>
          {statusTransitions.map(({ label, next }) => (
            <Col key={next}>
              <Popconfirm
                title={`Set template to "${STATUS_CONFIG[next]?.label}"?`}
                onConfirm={() => handleStatusChange(next)}
                okText="Yes"
                cancelText="No"
              >
                <Button size="small" type={next === 'ACTIVE' ? 'primary' : 'default'}>
                  {label}
                </Button>
              </Popconfirm>
            </Col>
          ))}
        </Row>
      }
      style={{ maxWidth: 900, margin: "auto" }}
      bodyStyle={{ padding: 0 }}
    >
      {contextHolder}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={(changed) => {
          if (changed.entityType !== undefined) {
            setSelectedEntityType(changed.entityType || null);
            // Auto-populate lifecycle steps for INVENTORY_TRANSFER_APPROVAL
            if (changed.entityType === 'INVENTORY_TRANSFER_APPROVAL') {
              const currentSteps = form.getFieldValue('steps') || [];
              // Only auto-populate if the steps don't already match the lifecycle
              const isAlreadyPopulated = currentSteps.length === 6
                && currentSteps.every((s, i) => s.stepName === INVENTORY_TRANSFER_LIFECYCLE_STEPS[i].stepName);
              if (!isAlreadyPopulated) {
                form.setFieldsValue({ steps: INVENTORY_TRANSFER_LIFECYCLE_STEPS.map(s => ({ ...s })) });
              }
            }
          }
        }}
        initialValues={{ autoApprove: false, steps: [{ approvalMode: 'SEQUENTIAL', escalationStrategy: 'REASSIGN', commentsRequired: false, resolvers: [{}] }] }}
        style={{ padding: 24 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          tabBarGutter={32}
          items={[
            {
              key: "1",
              label: "General",
              children: canEditField(module, 'details') ? (
                <ModuleDetailsFormSection entityTypeOptions={entityTypeOptions} />
              ) : noPermission,
            },
            {
              key: "2",
              label: "Approval Chain",
              children: canEditField(module, 'approvers') ? (
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.autoApprove !== cur.autoApprove}>
                  {({ getFieldValue }) =>
                    getFieldValue("autoApprove") ? (
                      <Card bordered={false}>
                        <Text type="secondary">
                          Auto Approve is enabled — no approver steps are needed. Steps defined here will be ignored on save.
                        </Text>
                      </Card>
                    ) : (
                    <ApproversSection
                        roleOptions={roleOptions}
                        strategyOptions={strategyOptions}
                        userOptions={userOptions}
                        fixedSteps={selectedEntityType === 'INVENTORY_TRANSFER_APPROVAL'}
                        entityType={selectedEntityType}
                      />
                    )
                  }
                </Form.Item>
              ) : noPermission,
            },
            {
              key: "3",
              label: "Rules",
              children: canEditField(module, 'conditions') ? (
                <ApprovalRulesSection
                  factOptions={
                    selectedEntityType
                      ? factCatalogue.filter((f) => f.entityType === selectedEntityType)
                      : factCatalogue
                  }
                />
              ) : noPermission,
            },
            {
              key: "4",
              label: "Preview",
              children: (
                <Card bordered={false}>
                  <Title level={5}>Workflow Visualizer</Title>
                  <Text type="secondary">
                    Future capability: This section will visually map out the workflow path and rules.
                  </Text>
                  {/* Basic dummy render of the current steps for now */}
                  <div style={{ marginTop: 24 }}>
                    <Form.Item shouldUpdate>
                      {({ getFieldValue }) => {
                        const currentSteps = getFieldValue('steps') || [];
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {currentSteps.map((s, i) => (
                              <Card size="small" key={i}>
                                <strong>Step {i + 1}:</strong> {s.stepName || 'Unnamed Step'} 
                                <span style={{ float: 'right' }}>{s.assignmentStrategy}</span>
                              </Card>
                            ))}
                          </div>
                        );
                      }}
                    </Form.Item>
                  </div>
                </Card>
              )
            }
          ]}
        />
        <Row justify="end" gutter={16} style={{ marginTop: 32 }}>
          <Col>
            <Button
              onClick={() => navigate("/approvals/approval-templates")}
              style={{ minWidth: 100 }}
            >
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              htmlType="reset"
              onClick={() => form.resetFields()}
              style={{ minWidth: 100 }}
            >
              Reset
            </Button>
          </Col>
          {id ? (
            <>
              <Col>
                <Button type="primary" htmlType="submit" style={{ minWidth: 120 }}>
                  Update Template
                </Button>
              </Col>
              <Col>
                <Button type="default" onClick={handleCloneAsNew} style={{ minWidth: 150 }}>
                  Save as New Version (Clone)
                </Button>
              </Col>
            </>
          ) : (
            <Col>
              <Button type="primary" htmlType="submit" style={{ minWidth: 120 }}>
                Create Template
              </Button>
            </Col>
          )}
        </Row>
      </Form>
    </Card>
  );
};

export default ApprovalTemplateForm;
